import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { GetOrganizationBrandingQuery } from "src/generated/graphql";
import { IMantineColor, generateShadesTintsColors } from "src/utils/theme";

const DEFAULT_PRIMARY = [
  "#363636",
  "#303030",
  "#2A2A2A",
  "#262626",
  "#222222",
  "#1E1E1E",
  "#1B1B1B",
  "#181818",
  "#151515",
  "#121212",
] as IMantineColor;

const DEFAULT_SECONDARY = [
  "#fff",
  "#f8f8f8",
  "#f2f2f2",
  "#ddd",
  "#d1d1d1",
  "#bfbfbf",
  "#B3B3B3",
  "#ADADAD",
  "#9a9a9a",
  "#777",
] as IMantineColor;

interface BrandingContextType {
  branding: GetOrganizationBrandingQuery["organizationBrandingOne"] | null;
  setBranding: (
    content: GetOrganizationBrandingQuery["organizationBrandingOne"] | null
  ) => void;
  primaryColors: IMantineColor;
  secondaryColors: IMantineColor;
  orgIdLogin: string;
  setorgIdLogin: (e: string) => void;

  isFlatPage: boolean;
  setIsFlatPage: (e: boolean) => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined
);

export const useBrandingContext = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
}) => {
  const [branding, setBranding] = useState<
    GetOrganizationBrandingQuery["organizationBrandingOne"] | null
  >(null);
  const [orgIdLogin, setorgIdLogin] = useState("");
  const [primaryColors, setPrimaryColors] =
    useState<IMantineColor>(DEFAULT_PRIMARY);
  const [secondaryColors, setSecondaryColors] =
    useState<IMantineColor>(DEFAULT_SECONDARY);
  const [isFlatPage, setIsFlatPage] = useState(false);

  useEffect(() => {
    if (branding?.primaryColor) {
      setPrimaryColors(generateShadesTintsColors(branding.primaryColor));
    }
    if (branding?.secondaryColor) {
      setSecondaryColors(generateShadesTintsColors(branding.secondaryColor));
    }
  }, [branding]);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        setBranding,
        primaryColors,
        secondaryColors,
        orgIdLogin,
        setorgIdLogin,
        isFlatPage,
        setIsFlatPage,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};
